import { useEffect, useState } from "react";
import { CheckCircle2, FileSignature, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "./SignaturePad";

type QueueItem = {
  requestId: string;
  title: string;
  recipientName: string;
  vertical: string;
  recipientSignedAt: string;
  expiresAt: string;
};

async function readJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(body.error ?? "The agreement request failed.");
  return body;
}

export default function AgreementCompanyQueue() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [selected, setSelected] = useState<QueueItem | null>(null);
  const [documentUrl, setDocumentUrl] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [usingApprovedSignature, setUsingApprovedSignature] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      setItems(await readJson(await fetch("/api/agreements/company/queue")));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Queue unavailable."
      );
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    let url = "";
    let cancelled = false;
    setSignature(null);
    setUsingApprovedSignature(false);
    setDocumentUrl("");
    if (selected) {
      void fetch(`/api/agreements/company/${selected.requestId}/document`)
        .then(async response => {
          if (!response.ok) throw new Error((await response.json()).error);
          url = URL.createObjectURL(await response.blob());
          setDocumentUrl(url);
        })
        .catch(error => toast.error(error.message));
      void fetch(
        `/api/agreements/company/${selected.requestId}/approved-signature`
      )
        .then(async response => {
          if (!response.ok) return;
          const blob = await response.blob();
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          });
          if (!cancelled) {
            setSignature(dataUrl);
            setUsingApprovedSignature(true);
          }
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [selected]);

  const submit = async () => {
    if (!selected || !signature || !consent) return;
    setBusy(true);
    try {
      const response = await fetch(
        `/api/agreements/company/${selected.requestId}/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            consentAccepted: true,
            fields: {},
            idempotencyKey: crypto.randomUUID(),
            signaturePngBase64: signature.split(",")[1] ?? "",
          }),
        }
      );
      await readJson(response);
      toast.success("Company signature recorded.");
      setSelected(null);
      setSignature(null);
      setUsingApprovedSignature(false);
      setConsent(false);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Signing failed.");
    } finally {
      setBusy(false);
    }
  };

  if (selected)
    return (
      <div className="space-y-5">
        <button
          onClick={() => setSelected(null)}
          className="text-xs font-semibold text-gray-500 hover:text-gray-900"
        >
          Back to signing queue
        </button>
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {documentUrl ? (
              <iframe
                title="Recipient-signed agreement"
                src={documentUrl}
                className="h-[70vh] w-full"
              />
            ) : (
              <div className="grid h-[70vh] place-items-center text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="sr-only">Loading recipient-signed agreement</span>
              </div>
            )}
          </div>
          <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {selected.title}
              </h2>
              <p className="text-sm text-gray-500">
                Recipient: {selected.recipientName}
              </p>
            </div>
            {usingApprovedSignature && signature ? (
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="mb-3 text-xs font-semibold text-gray-700">
                  Approved company signature
                </p>
                <img
                  src={signature}
                  alt="Approved company signature"
                  className="h-24 w-full object-contain"
                />
                <p className="mt-3 text-xs leading-5 text-gray-500">
                  This is the verified countersignature supplied in the revised
                  Master Agent Agreement. Review the agreement before using it.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSignature(null);
                    setUsingApprovedSignature(false);
                  }}
                  className="mt-3 text-xs font-semibold text-gray-600 underline"
                >
                  Draw a different signature
                </button>
              </div>
            ) : (
              <SignaturePad theme="light" onChange={setSignature} />
            )}
            <label className="flex gap-3 rounded-xl border border-gray-200 bg-white p-3 text-xs leading-5 text-gray-600">
              <input
                type="checkbox"
                checked={consent}
                onChange={event => setConsent(event.target.checked)}
                className="mt-1"
              />
              <span>
                I reviewed the recipient-signed agreement and am signing with
                the authority assigned to my Team Portal account.
              </span>
            </label>
            <button
              disabled={busy || !signature || !consent}
              onClick={submit}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#84ab4a] px-4 py-3 text-sm font-semibold text-white disabled:opacity-40"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Countersign agreement
            </button>
          </aside>
        </div>
      </div>
    );

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Agreement signing queue
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Agreements assigned to you after the recipient has signed.
          </p>
        </div>
        <button
          onClick={load}
          disabled={busy}
          className="rounded-lg border border-gray-200 p-2 text-gray-500"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="divide-y divide-gray-100 border-y border-gray-100">
        {items.length === 0 ? (
          <div className="py-14 text-center">
            <FileSignature className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">
              No agreements are waiting for your signature.
            </p>
          </div>
        ) : (
          items.map(item => (
            <button
              key={item.requestId}
              onClick={() => setSelected(item)}
              className="flex w-full items-center justify-between gap-4 px-2 py-4 text-left hover:bg-gray-50"
            >
              <div>
                <div className="font-semibold text-gray-900">{item.title}</div>
                <div className="mt-1 text-xs text-gray-500">
                  {item.recipientName} · {item.vertical}
                </div>
              </div>
              <div className="text-right text-xs text-gray-400">
                Recipient signed
                <br />
                {new Date(item.recipientSignedAt).toLocaleDateString()}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
